import * as React from 'react';
import { flow, forEach, get, head, startsWith } from 'lodash';
import { change, formValueSelector, getFormSyncErrors, reduxForm, submit } from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { actions as quoteActions, actions } from '../../../../services/quote/reducer';
import { push } from 'react-router-redux';
import { CommonPayment, validate } from '../Payment/common';
import { isRequired } from '../../../../util/validators';
import { renewalTypeSelectionEnum } from '../Payment/components/YourRenewal';
import { Button, Col, Form, Row } from 'react-bootstrap';
import Signposting, { SignpostType } from '../../components/Signposting';
import { FaSpinner } from 'react-icons/fa';
import './style.scss';
import { shouldIgnoreCardPermissions } from '../../../../helpers/Quote';
import MedicalImportantSection from '../QuoteSummary/MedicalImportantSection';
import EmergencyMessaging, { EmergencyMessagingEnum } from '../../../../components/EmergencyMessaging';
import Loading from '../../../../components/Loading';
import QuoteSummaryHeader from '../QuoteSummary/components/QuoteSummaryHeader';
import QuoteSummary from '../QuoteSummary';
import {Compliance} from '../Payment/components/Compliance';
import MarketingOptions from '../Payment/components/MarketingOptions';
import {PaymentConsent} from '../../../../components/PaymentConsent';
import {BsInfoCircle} from 'react-icons/bs';
import DataLayer from '../../../../helpers/DataLayer';
import {IPaymentWallet, IPaymentWalletProvider} from '../../../../data/api/models';
import {GooglePayWrapper} from '../../../../components/PayWithWallet';
import ApplePayWrapper from '../../../../components/PayWithWallet/components/ApplePayWrapper';
import FuelShortageWarning from '../../components/FuelShortageWarning';

class AggPayment extends CommonPayment {

  state = {
    loading: false,
    selectedPaymentMethod: 'none',
  };

  constructor(props) {
    super(props);
    const { actions } = props;
    actions.change('quote', 'policyDeclaration', true);
    this.recalculatePrice();
  }

  componentDidMount() {
    DataLayer.handlePageView();
  }

  private recalculatePrice = () => {
    const { actions } = this.props;

    actions.change('quote', 'preventPatch', true);

    this.setState({ loading: true })
    actions.quote.calculate(null, true).then(() => {
      this.setState({ loading: false });
    });
  }

  private renderEmergencyMessage = () => {
    return (
      <EmergencyMessaging type={EmergencyMessagingEnum.AGGREGATOR}/>
    )
  }

  private handleBack = () => {
    window.history.back();
  }

  private handleGooglePayClick = () => {
    this.handlePaymentMethodClick('google');
    this.props.change('selectedPaymentMethod', 'google');
  };

  private handleApplePayClick = () => {
    this.handlePaymentMethodClick('apple');
    this.props.change('selectedPaymentMethod', 'apple');
  };

  private handleCardClick = () => {
    this.handlePaymentMethodClick('card');
    this.props.change('selectedPaymentMethod', 'card');
  };

  private handlePaymentMethodClick = (method: 'card' | 'google' | 'apple') => {
    this.setState({ selectedPaymentMethod: method });
  };

  private renderGooglePayButton = () => {
    const {premium, change, paymentWalletProviderConfigData} = this.props;

    if (!premium) {
      return null;
    }

    if (!paymentWalletProviderConfigData.googlePay.enabled) {
      return null;
    }

    const onGooglePayLoadPaymentData = (paymentData: google.payments.api.PaymentData) => {
      const paymentWallet: IPaymentWallet = {
        provider: IPaymentWalletProvider.GOOGLE_PAY,
        data: paymentData,
      };

      change('paymentWallet', paymentWallet);
      change('permissions', false);
      this.checkForErrors();
    };

    return (
      <div onClick={this.handleGooglePayClick}>
        <GooglePayWrapper
          grossPremium={premium.gross.toString()}
          onGooglePayLoadPaymentData={onGooglePayLoadPaymentData}
          paymentWalletProviderConfigData={paymentWalletProviderConfigData}
        />
      </div>
    );
  };

  private renderApplePayButton = () => {
    const { premium, change, paymentWalletProviderConfigData} = this.props;

    if (!premium) {
      return null;
    }

    if (!paymentWalletProviderConfigData.applePay.enabled) {
      return null;
    }

    const onPaymentAuthorized = (
      paymentData: ApplePayJS.ApplePayPayment,
    ): Promise<any> => {

      const paymentWallet: IPaymentWallet = {
        provider: IPaymentWalletProvider.APPLE_PAY,
        data: paymentData,
      };

      change('paymentWallet', paymentWallet);
      change('permissions', false);

      return this.checkForErrors();
    };


    return (
      <ApplePayWrapper
        grossPremium={premium.gross.toString()}
        paymentWalletProviderConfigData={paymentWalletProviderConfigData}
        onPaymentAuthorized={onPaymentAuthorized}
        onClick={this.handleApplePayClick}
      />
    );
  };

  private renderDigitalWalletPaymentOptions = () => {
    if (!this.walletsAvailable()) {
      return null;
    }

    return (
      <div className="gateway-payment-container">
        {this.renderGooglePayButton()}
        {this.renderApplePayButton()}

        <Button
          bsSize="lg"
          className="gateway-payment-button card-button"
          onClick={this.handleCardClick}
        >
          Card
        </Button>
      </div>
    );
  };

  private walletsAvailable = () => {
    const { quote, paymentWalletProviderConfigData } = this.props;
    return quote.application.tripType === 'single'
      && (paymentWalletProviderConfigData.applePay.enabled || paymentWalletProviderConfigData.googlePay.enabled)
      && this.isInWalletTestGroup();
  };

  public render() {
    const {
      product,
      submitting,
      permissions,
      i18n,
      change,
      actions,
      handleSubmit,
      valid,
      dispatch,
      reuseCustomerAddress,
      submitFailed,
      quote,
      password,
      passwordConfirmation,
      errorMessage,
      channel,
      premium,
      quoteReference,
      travellers,
      renderDefinition,
      quoteType,
      renewalStatus,
      medicalsDeclared,
      billing,
    } = this.props;

    const { loading } = this.state;

    const leadTraveller = quote.application.travellers[0];
    const customer = quote.customer;

    const hasCustomer = !!customer;
    const isIOM = leadTraveller.address && startsWith(leadTraveller.address.postcode, 'IM');

    const disableSubmit = (customer.status === 'new' && password !== passwordConfirmation) || isIOM;

    const formHasError = isRequired && submitFailed && !valid;
    const isAggregator = channel.channelType === 'AGG';
    const ignoredChannelKeys = ['idol', 'go-compare', 'compare-the-market', 'confused', 'uswitch', 'money']
    const shouldShowSignPost = isAggregator && !ignoredChannelKeys.includes(channel.key);

    const isSignposted = get(premium, 'isSignposted', false);
    const shouldShowSection = quote.application.tripType === 'annual' && quoteType !== 'renewal';
    const ignorePermissions = shouldIgnoreCardPermissions();
    const hasMedical = this.hasTravellerMedical()
    const referrals = get(premium, 'referrals', []);

    let fraudMessage = '';
    if (errorMessage && errorMessage.toLowerCase().includes('fraud')) {
      fraudMessage = 'Card Payment was unsuccessful: It appears that you are abroad already. Unfortunately we are unable to accept payments if you are already overseas. If this is not the case please call our customer services team on 0333 234 9913';
    }

    if (!permissions && !ignorePermissions) {
      actions.change('quote', 'renewalStatus', renewalTypeSelectionEnum.MANUAL)
    }

    const schemeType = get(premium, 'schemeType');
    const premiumGross = get(premium, 'gross');

    if (loading) {
      return (
        <Loading/>
      )
    }

    if (referrals.length > 0) {
      return (
        <div style={{ textAlign: 'center' }}>
          <h1>Sorry, something went wrong</h1>
          <p>
            Unfortunately there has been a problem with your quote , please see
            our <a className="pointer" href={'https://www.admiral.com/contact-us/travel'} target={'_blank'}>Contact
            Us</a> page for
            details on how to get in touch.
          </p>
        </div>
      );
    }

    return (
      <div className={'payment-page'}>
        <QuoteSummaryHeader
          tripType={this.props.tripType}
          premium={this.props.premium}
          quoteReference={this.props.quoteReference}
          schemeId={this.props.schemeId}
        />
        <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">
          {(formHasError || errorMessage) && (
            <div className="error-block-container" id="form-errors">
              <h1>Whoops!</h1>
              {formHasError && (
                <p>Missing something? Please check the areas marked in orange before you continue.</p>)}
              {errorMessage && (
                <p>{fraudMessage ? fraudMessage : (errorMessage !== 'Payment Error' ? errorMessage : '')}
                </p>
              )}
              {errorMessage && errorMessage === 'Payment Error' && (
                <Row>
                  <Col>
                    <p>We have been unable to process your payment request.</p>
                    <p>No Payment has been taken</p>
                    <p>No cover has been set up</p>
                    <p>Your details have been saved – quote Ref {quoteReference}</p>
                    <p>Please try again or <a className="pointer"
                                              href={'https://www.admiral.com/contact-us/travel'}
                                              target={'_blank'}>contact us</a> so we can help you
                      further.
                    </p>
                  </Col>
                </Row>

              )}
            </div>
          )}

          {isIOM && (
            <div className="error-block-container">
              <p>
                {`If you would like to continue a quote or purchase, please contact our call-centre on ${channel.phone}.`}
              </p>
            </div>
          )}

          <div className="steps-container payment-step-container">

            {this.renderEmergencyMessage()}

            <div className={'steps-container payment-step-container'}>
              <QuoteSummary
                channel={channel}
                isAggregator={isAggregator}
                product={product}
                quote={quote}
                selectedPremium={premium}
                renderDefinition={renderDefinition}
                quoteType={quoteType}
                permissions={permissions}
                change={change}
                dispatch={dispatch}
                isAnnual={quote.application.tripType === 'annual'}
              />
            </div>

            {shouldShowSignPost && isSignposted && (
              <div style={{marginBottom: '20px'}} className={'section-space'}>
                <Signposting type={SignpostType.TYPE_A} withCheckbox={true}
                             checkBoxFormPath={'signpost_consent'}/>
              </div>
            )}

            <MedicalImportantSection
              hasTravellerDeclaredCondition={hasMedical}
              isAggregator={true}
              {...this.props}
            />

            <div className={'section-group'}>
              <div className={'quote-card-title'}>
                Terms and Conditions
              </div>

              <div className={'section-group-content'}>
                <Compliance/>
                <div className={'important-title'} style={{margin: '20px 0px'}}>Please make sure you don’t already have
                  cover in place.
                </div>
              </div>
            </div>

            <FuelShortageWarning />

            <MarketingOptions formName={this.props.form} change={change}/>

            <div className={'section-group'}>
              <div className={'quote-card-title'}>
                Policy Document Preferences
              </div>

              <div className={'section-group-content'}>
                {this.renderPrintOptions()}
              </div>
            </div>

            <div className="btn-bar btn-bar--auto-height">
              <div className={'section-group'}>
                <div className="quote-card-title">
                  Payment
                </div>
                <div className={'section-group-content'}>
                  {(!this.walletsAvailable() || this.state.selectedPaymentMethod === 'card') && (
                    <>
                      <PaymentConsent
                        travellers={travellers}
                        shouldShowRenewalSection={shouldShowSection}
                        channel={channel}
                        ignorePermissions={ignorePermissions}
                        permissions={permissions}
                        billing={billing}
                        renderDefinition={renderDefinition}
                        dispatch={dispatch}
                        renewalStatus={renewalStatus}
                        {...this.props}
                      />
                    </>
                  )}
                  {this.renderTrackingItems()}
                  <div style={{display: medicalsDeclared === false ? 'none' : ''}}>
                    <div>
                      {this.renderPremium()}
                      {this.renderDigitalWalletPaymentOptions()}
                    </div>
                    {(!this.walletsAvailable() || this.state.selectedPaymentMethod === 'card') && (
                      <>
                        <div className={'card-details-helper'}>
                          <span><BsInfoCircle/></span> Card details can be entered in
                          the next step
                        </div>
                      </>
                    )}
                    <div className="payment-buttons">
                      <div className="purchase-button-wrapper">
                        <Button
                          type="submit"
                          bsSize="lg"
                          className="purchase-button"
                          onClick={this.checkForErrors}
                          disabled={
                            submitting ||
                            (submitFailed && !valid) ||
                            disableSubmit ||
                            !hasCustomer ||
                            quote.waitForPurchaseToComplete ||
                            (this.walletsAvailable() && this.state.selectedPaymentMethod !== 'card')
                          }
                        >
                          {quote.waitForPurchaseToComplete && (
                            <FaSpinner className="fa-spin"/>
                          )} PAY NOW
                        </Button>
                      </div>
                      <div className="back-button-wrapper">
                        <Button
                          bsSize="lg"
                          className="back-button"
                          onClick={this.handleBack}
                          disabled={
                            submitting
                          }
                        >
                          BACK
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    );
  }
}

let selector;
export default flow([
  reduxForm({
    form: 'quote',
    validate,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const channel = state.branding.channel;
      const product = head(get(state, 'branding.channel.products', []));
      const quotes = state.quote.quotes;
      const errorMessage = state.quote.errorMessage;
      const synchronousError = getFormSyncErrors('quote')(state);
      const schemeId = selector(state, 'schemeId');
      const password = selector(state, 'password');
      const passwordConfirmation = selector(state, 'passwordConfirmation');
      const policyDeclaration = selector(state, 'policyDeclaration');
      const paymentInfo = selector(state, 'payment');
      const startDate = selector(state, 'startDate');
      const endDate = selector(state, 'endDate');
      const post = selector(state, 'communications.post');
      const fraudCheck = state.quote.application.fraudCheck;
      const options = selector(state, 'options');
      const visibility = selector(state, 'visibility');
      const payment = selector(state, 'payment');
      const permissions = selector(state, 'permissions');
      const applicationId = selector(state, 'id');
      const quoteReference = selector(state, 'quoteReference');
      const reuseCustomerAddress = selector(state, 'reuseCustomerAddress');
      const travellers = selector(state, 'travellers');
      const quoteType = selector(state, 'quoteType');
      const documents = selector(state, 'documents');
      const renewalStatus = selector(state, 'renewalStatus');
      const medicalsDeclared = selector(state, 'medicalsDeclared');
      const tripType = selector(state, 'tripType');
      const billing = selector(state, 'billing');
      const paymentWalletProviderConfigData = state.quote.paymentWalletProviderConfigData;

      let premium = null;

      forEach(quotes, (tripTypes, level) => {
        forEach(tripTypes, (quotedPremium) => {
          if (quotedPremium.scheme.id === schemeId) {
            premium = quotedPremium;
          }
        });
      });

      return {
        schemeId,
        premium,
        channel,
        policyDeclaration,
        password,
        passwordConfirmation,
        errorMessage,
        paymentInfo,
        startDate,
        endDate,
        post,
        fraudCheck,
        options,
        visibility,
        synchronousError,
        payment,
        permissions,
        applicationId,
        quoteReference,
        reuseCustomerAddress,
        travellers,
        quoteType,
        documents,
        product,
        renewalStatus,
        medicalsDeclared,
        tripType,
        billing,
        paymentWalletProviderConfigData,
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        submit: bindActionCreators(submit, dispatch),
        calculate: bindActionCreators(actions.calculate, dispatch),
        push: bindActionCreators(push, dispatch),
        quote: bindActionCreators({...quoteActions}, dispatch),
        change: bindActionCreators(change, dispatch),
      },
    }),
  ),
])(AggPayment);
